import { addressNeedsUnit } from "./candidateSelection";
import {
  buildUndoPayload,
  savedDiffersFromSeen,
} from "./standardizationDiff";

/**
 * The post-verdict commit decision for a hold-mode address Save — the single
 * source of truth for "given what the user saw, what the captured autofill was,
 * and how Smarty's verification settled, what do we POST and was there a
 * surprise to undo?"
 *
 * Both hold-save callers funnel through here so the branching can't drift:
 *  - {@link useStandardizedSave}'s `handleSave` (admin profile + Network), which
 *    POSTs the result via `onSave` and stashes the undo payload, and
 *  - {@link AddressField}'s `commitRef` (boards cell + transfers popovers),
 *    which returns the decision to the popover so it can POST and host the
 *    callout slides.
 *
 * Each caller still sources its own inputs and owns its own side effects (paint
 * the unit slot, POST, stash/return the undo). This function is pure: it reads
 * nothing and writes nothing — it only classifies.
 *
 * The two callers source `typed` differently and that difference is deliberate,
 * not drift: the engine's Save-time autofill is held out of the visible buffer
 * (captured here as `captured`), but the keyboard-parity ghost freeze
 * (`commitVisibleGhosts`) IS painted before the verdict resolves. `handleSave`
 * reads `typed` before that paint flushes (so its undo restores the bare typed
 * tokens); `AddressField` reads it after (so its undo restores the frozen "seen"
 * text — typed tokens + the gray ghost suffix). Both are "what the user saw" for
 * their surface; this function takes whatever they pass.
 *
 * @param {object}   params
 * @param {object}   params.typed       - what the user saw (per the note above)
 * @param {object|null} params.captured - the Save-time autofill delta held out
 *   of the visible buffer; null when nothing was autofilled (not dirty / clean)
 * @param {{ kind: string, baseRetain?: object, bareBuilding?: object }} params.verdict
 *   - the engine's verification verdict (see `awaitVerificationVerdict`)
 * @param {(base: object, delta: object) => object} params.merge - the surface's
 *   address merge (`mergeHookDelta` for addr1-native surfaces,
 *   `mergeCountryIDAddressDelta` for the numeric-`countryID` ones)
 * @param {string}   [params.streetField="addr1"] - the key the street lives
 *   under in this surface's value shape
 * @returns {{ kind: "deferred" }
 *   | { kind: "noChange", unverifiable: boolean }
 *   | { kind: "unitSlot", standardized: object }
 *   | { kind: "commit", standardized: object, standardization: object|null, unverifiable: boolean }}
 */
export default function resolveStandardizedCommit({
  typed,
  captured,
  verdict,
  merge,
  streetField = "addr1",
}) {
  // The fetch never settled in the poll window — the caller holds its editor
  // open rather than POSTing an unresolved buffer. (Defensive: the verdict
  // force-settles unverifiable now, so this is rarely reached.)
  if (verdict?.kind === "deferred") return { kind: "deferred" };

  const unverifiable = verdict?.kind === "unverifiable";

  // No autofill (not dirty / nothing to verify): POST the typed buffer as-is.
  if (!captured) return { kind: "noChange", unverifiable };

  const standardized = merge(typed, captured);

  // The autofill introduced an "…Apt ___" slot the user still owes (and the
  // typed value didn't already carry one). Unlike a standardization this MUST
  // land in the visible field so the user can fill the placeholder — the caller
  // paints it and stops short of saving the sentinel.
  if (addressNeedsUnit(standardized) && !addressNeedsUnit(typed))
    return { kind: "unitSlot", standardized };

  // Base-retain (appended unit) OR bare-building (its sibling) canonicalized the
  // street while leaving the address unverifiable — the undo payload rides the
  // verdict and fires ONLY when the STREET tokens actually changed, never on
  // auto-filled locality. The engine's payload is always `addr1`-shaped, so map
  // it back to a `street`-keyed surface (no-op for the addr1-native ones).
  // Otherwise it's an ordinary verify standardization: surface the undo when the
  // saved address differs from what the user *saw* (typed text + ghost).
  const streetCommit = verdict?.baseRetain || verdict?.bareBuilding;
  let standardization;
  if (streetCommit) {
    standardization = streetCommit.streetStandardized
      ? streetCommit.undoPayload
      : null;
    if (standardization && streetField !== "addr1")
      standardization = merge(standardized, standardization);
  } else {
    standardization = savedDiffersFromSeen(typed, standardized, streetField)
      ? buildUndoPayload(typed, standardized, streetField)
      : null;
  }

  return { kind: "commit", standardized, standardization, unverifiable };
}
